import moment from "moment-timezone";
import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import { currencyFormatter, getRecentBillsStatus, handleProductName } from "_helpers";
import Indicator from "components/Indicator";
import useIsDesktopSize from "_hooks/useIsDesktopSize";
import breakpoints from "_constants/breakpoints";

const StyledContainer = styled.div`
  width: 90%;
  max-width: 840px;
  font-size: 1rem;

  @media (max-width: ${breakpoints.mobileM}) {
    width: 100%;
    font-size: 0.825rem;
  }
`;

const BillCard = styled.div`
  margin-bottom: 24px;
  padding: 12px;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  width: 100%;
`;

const BillHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 2px solid lightgray;
  color: #7b848d;
`;

const BillFooter = styled.div`
  display: flex;
  /* flex-direction: column; */
  justify-content: space-between;
  align-items: flex-end;
  border-top: 2px solid lightgray;
  padding: 8px 8px 0;

  @media (max-width: ${breakpoints.mobileL}) {
    flex-direction: column;
  }
`;

const BillFooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BillFooterItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const BillFooterItemSecondary = styled(BillFooterItem)`
  font-size: 0.875em;
  color: #7b848d;
`;

const BillFooterItemColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:last-child {
    align-items: flex-end;
    min-width: 64px;
  }
`;

const ProductRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  font-size: 0.875em;

  &:not(:last-child) {
    border-bottom: 1px solid lightgray;
  }
`;

const ProductName = styled.div`
  font-weight: 400;
  margin-bottom: 0;

  @media (max-width: ${breakpoints.laptop}) {
    font-weight: 500;
    margin-bottom: 4px;
  }
`;

const StatusContainer = styled.div`
  display: flex;
  gap: 14px;
`;

const IndicatorContainer = styled.div`
  display: flex;
  align-items: center;
  height: 1.15rem;
`;

const StatusColumn = styled.div`
  flex: 2;
  display: flex;
  align-items: flex-start;
  gap: 4px;

  @media (max-width: ${breakpoints.laptop}) {
    flex: 4;
  }
`;

const ProductColumn = styled.div`
  flex: 4;
  display: flex;

  @media (max-width: ${breakpoints.laptop}) {
    flex: 5;
  }
`;

const DateColumn = styled.div`
  flex: 4;
  display: flex;

  @media (max-width: ${breakpoints.laptop}) {
    flex: 5;
  }
`;

const PriceColumn = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const DescriptorColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 5;
  word-break: break-all;

  @media (max-width: ${breakpoints.laptop}) {
    flex: 6;
    margin-bottom: 8px;
  }
`;

const DescriptorLabel = styled.div`
  font-size: 0.7em;
  font-style: italic;
  font-weight: 400;
`;

const Price = styled.div`
  font-weight: 400;
`;

const OrderIdTitle = styled.div`
  font-weight: 700;
  font-size: 0.875em;
`;

const OrderId = styled.span`
  font-size: 1em;
  color: black;
  font-weight: 400;
`;

const Date = styled.div`
  font-style: italic;
`;

const DateMonospace = styled(Date)`
  font-family: monospace;
`;

function RecentBillsTable(props) {
  const { bills } = props;

  const isDesktop = useIsDesktopSize();

  return (
    <StyledContainer>
      {bills?.map((bill, idx) => (
        <BillCard key={`bill-card-${idx}`}>
          <BillHeader>
            <OrderIdTitle>
              Order ID: <OrderId>#{bill?.crmOrderId}</OrderId>
            </OrderIdTitle>

            <Date title={bill?.orderDate}>
              {isDesktop
                ? moment(bill?.orderDate).format("MMMM DD, YYYY")
                : moment(bill?.orderDate).format("MMM DD, YYYY")}
            </Date>
          </BillHeader>
          <div>
            {bill?.products?.map((product, idx) => (
              <ProductRow key={`bill-product-row-${idx}`}>
                {!isDesktop && <ProductName>{handleProductName(product?.code?.customerName)}</ProductName>}
                {!isDesktop && (
                  <DescriptorColumn>
                    <DescriptorLabel>Appears on Your Statement As:</DescriptorLabel>
                    {product?.merchantDescriptor}
                  </DescriptorColumn>
                )}

                {Boolean(product?.refundAmount) && (
                  <StatusContainer>
                    <StatusColumn>
                      <IndicatorContainer>
                        <Indicator status={product?.paymentStatus} />
                      </IndicatorContainer>
                      <div title={product?.paymentStatus}>
                        {getRecentBillsStatus(product?.paymentStatus) || product?.paymentStatus}
                      </div>
                    </StatusColumn>
                    {isDesktop && (
                      <ProductColumn>
                        <ProductName>{handleProductName(product?.code?.customerName)}</ProductName>
                      </ProductColumn>
                    )}
                    <DateColumn>
                      <DateMonospace>
                        {product?.refundDate ? moment(product?.refundDate).format("MM/DD/YYYY h:mm A") : ""}
                      </DateMonospace>
                    </DateColumn>
                    <PriceColumn>
                      <Price>{currencyFormatter(product?.refundAmount)}</Price>
                    </PriceColumn>
                    {isDesktop && (
                      <DescriptorColumn>
                        <DescriptorLabel>Appears on Your Statement As:</DescriptorLabel>
                        {product?.merchantDescriptor}
                      </DescriptorColumn>
                    )}
                  </StatusContainer>
                )}

                <StatusContainer>
                  <StatusColumn>
                    <IndicatorContainer>
                      <Indicator status={product?.displayedPaymentStatus} />
                    </IndicatorContainer>
                    <div title={product?.displayedPaymentStatus}>
                      {getRecentBillsStatus(product?.displayedPaymentStatus)}
                    </div>
                  </StatusColumn>
                  {isDesktop && (
                    <ProductColumn>
                      {!product?.refundAmount && (
                        <ProductName>{handleProductName(product?.code?.customerName)}</ProductName>
                      )}
                    </ProductColumn>
                  )}
                  <DateColumn>
                    <DateMonospace title={product?.paymentDate || "N/A"}>
                      {product?.paymentDate && <div>{moment(product?.paymentDate).format("MM/DD/YYYY h:mm A")}</div>}
                      {product?.isSubscription && (
                        <div>
                          {`Subscription Bill ${
                            Number.isInteger(product?.billNumber) ? `#${product?.billNumber}` : ""
                          }`}
                        </div>
                      )}
                    </DateMonospace>
                  </DateColumn>
                  <PriceColumn>
                    <Price>{currencyFormatter(product?.price)}</Price>
                  </PriceColumn>
                  {isDesktop && (
                    <DescriptorColumn>
                      <DescriptorLabel>Appears on Your Statement As:</DescriptorLabel>
                      {product?.merchantDescriptor}
                    </DescriptorColumn>
                  )}
                </StatusContainer>
              </ProductRow>
            ))}
          </div>
          <BillFooter>
            <BillFooterColumn>
              {(bill?.totalRefunded > 0 || bill?.totalChargeback > 0) && (
                <BillFooterItemSecondary>
                  <BillFooterItemColumn>Price:</BillFooterItemColumn>
                  <BillFooterItemColumn>{currencyFormatter(bill?.totalPrice)}</BillFooterItemColumn>
                </BillFooterItemSecondary>
              )}
              {bill?.totalRefunded > 0 && (
                <BillFooterItemSecondary>
                  <BillFooterItemColumn>Refund:</BillFooterItemColumn>
                  <BillFooterItemColumn>{currencyFormatter(bill?.totalRefunded)}</BillFooterItemColumn>
                </BillFooterItemSecondary>
              )}
              {bill?.totalChargeback > 0 && (
                <BillFooterItemSecondary>
                  <BillFooterItemColumn>Chargeback:</BillFooterItemColumn>
                  <BillFooterItemColumn>{currencyFormatter(bill?.totalChargeback)}</BillFooterItemColumn>
                </BillFooterItemSecondary>
              )}
            </BillFooterColumn>
            <BillFooterColumn>
              <BillFooterItem>
                <BillFooterItemColumn>Total:</BillFooterItemColumn>
                <BillFooterItemColumn>{currencyFormatter(bill?.totalAmount)}</BillFooterItemColumn>
              </BillFooterItem>
            </BillFooterColumn>
          </BillFooter>
        </BillCard>
      ))}
    </StyledContainer>
  );
}

RecentBillsTable.propTypes = {
  bills: PropTypes.array,
};

export default RecentBillsTable;
